<script lang="ts">
  import type { Person, PersonValidation } from "../../helpers/types";
  import Checkbox from "../atoms/Checkbox.svelte";
  import ErrorText from "../atoms/ErrorText.svelte";
  import Heading from "../atoms/Heading.svelte";
  import NumberInput from "../atoms/NumberInput.svelte";
  import PhoneInput from "../atoms/PhoneInput.svelte";
  import TextInput from "../atoms/TextInput.svelte";

  interface Props {
    person: Person;
    personValidation: PersonValidation;
    useSeperateAddress: boolean | undefined;
    disableInputs?: boolean;
    hasPressedContinue?: boolean;
    hasFetchedPersonAutomatic: boolean;
    requiresLimePurchase?: boolean;
  }

  let {
    person = $bindable(),
    personValidation,
    useSeperateAddress = $bindable(),
    disableInputs = false,
    hasPressedContinue = $bindable(false),
    hasFetchedPersonAutomatic = $bindable(),
    requiresLimePurchase,
  }: Props = $props();

  if (disableInputs) {
    useSeperateAddress = true;
  }
</script>

<Heading variant={"secondary"}>Dina kontaktuppgifter</Heading>
<div class="sm:flex sm:space-x-3 mb-2">
  {#if requiresLimePurchase}
    <div class="w-full mb-2 sm:mb-0">
      <label for="firstname" class="block">Namn</label>
      <TextInput
        disabled={disableInputs || hasFetchedPersonAutomatic}
        autoFocusDelay={false}
        id="firstname"
        name="firstname"
        required={hasFetchedPersonAutomatic ? false : true}
        {hasPressedContinue}
        class="w-full"
        error={personValidation.errors.firstname || (hasPressedContinue && !person.firstname)}
        bind:value={person.firstname}
      />
    </div>
  {:else}
    <div class="w-full mb-2 sm:mb-0">
      {#if personValidation.errors.firstname || (hasPressedContinue && !person.firstname)}
        <ErrorText>Fyll i förnamn</ErrorText>
      {:else}
        <label for="firstname" class="block">Förnamn</label>
      {/if}
      <TextInput
        disabled={disableInputs || hasFetchedPersonAutomatic}
        autoFocusDelay={false}
        id="firstname"
        name="firstname"
        required={hasFetchedPersonAutomatic ? false : true}
        {hasPressedContinue}
        class="w-full"
        error={personValidation.errors.firstname || (hasPressedContinue && !person.firstname)}
        bind:value={person.firstname}
      />
    </div>
    <div class="w-full">
      {#if personValidation.errors.lastname || (hasPressedContinue && !person.lastname)}
        <ErrorText>Fyll i efternamn</ErrorText>
      {:else}
        <label for="lastname" class="block">Efternamn</label>
      {/if}
      <TextInput
        disabled={disableInputs || hasFetchedPersonAutomatic}
        id="lastname"
        name="lastname"
        required={hasFetchedPersonAutomatic ? false : true}
        {hasPressedContinue}
        class="w-full"
        error={personValidation.errors.lastname || (hasPressedContinue && !person.lastname)}
        bind:value={person.lastname}
      />
    </div>
  {/if}
</div>
<div class="sm:flex mb-2 sm:space-x-3">
  <div class="w-full mb-2 sm:mb-0">
    {#if personValidation.errors.email || (hasPressedContinue && !person.email)}
      <ErrorText>Fyll i giltig e-postadress</ErrorText>
    {:else}
      <label for="mail" class="block">E-postadress</label>
    {/if}
    <TextInput
      disabled={disableInputs}
      autoFocusDelay={false}
      required={true}
      id="mail"
      name="email"
      class="w-full"
      {hasPressedContinue}
      error={personValidation.errors.email || (hasPressedContinue && !person.email)}
      bind:value={person.email}
    />
  </div>
  <div class="w-full">
    {#if personValidation.errors.mobile || (hasPressedContinue && !person.mobile)}
      <ErrorText>Fyll i mobilnummer</ErrorText>
    {:else}
      <label for="phone" class="block">Mobiltelefon</label>
    {/if}
    <PhoneInput
      disabled={disableInputs}
      class="w-full"
      required={true}
      {hasPressedContinue}
      error={personValidation.errors.mobile || (hasPressedContinue && !person.mobile)}
      name={"phone"}
      bind:value={person.mobile}
    />
  </div>
</div>
<div
  id="billingAdress"
  aria-expanded={useSeperateAddress}
  class="transition-all w-full {useSeperateAddress
    ? 'max-h-48 duration-700 opacity-100'
    : 'max-h-0 opacity-0 duration-300 overflow-hidden'}"
>
  <Heading variant="secondary">Faktureringsuppgifter</Heading>
  <div class="mb-4">
    {#if personValidation.errors.address || (hasPressedContinue && !person.address)}
      <ErrorText>Fyll i gatuadress</ErrorText>
    {:else}
      <label for="adress" class="block">Gatuadress</label>
    {/if}
    <TextInput
      id="adress"
      disabled={disableInputs}
      required={useSeperateAddress}
      name="adress"
      {hasPressedContinue}
      class="max-w-sm w-full"
      error={personValidation.errors.address || (hasPressedContinue && !person.address)}
      bind:value={person.address}
    />
  </div>
  <div class="mb-4 flex">
    <div class="mr-3">
      {#if personValidation.errors.zipcode || (hasPressedContinue && !person.zipcode)}
        <ErrorText>Fyll i postnummer</ErrorText>
      {:else}
        <label for="zipcode" class="block">Postnummer</label>
      {/if}
      <NumberInput
        max={99999}
        required={useSeperateAddress}
        disabled={disableInputs}
        {hasPressedContinue}
        id="zipcode"
        name="zipcode"
        class="max-w-sm w-full"
        error={personValidation.errors.zipcode || (hasPressedContinue && !person.zipcode)}
        bind:value={person.zipcode}
      />
    </div>
    <div>
      {#if personValidation.errors.city || (hasPressedContinue && !person.city)}
        <ErrorText>Fyll i postort</ErrorText>
      {:else}
        <label for="city" class="block">Postort</label>
      {/if}
      <TextInput
        id="city"
        disabled={disableInputs}
        name="city"
        {hasPressedContinue}
        required={useSeperateAddress}
        class="max-w-sm w-full"
        error={personValidation.errors.city || (hasPressedContinue && !person.city)}
        bind:value={person.city}
      />
    </div>
  </div>
</div>
{#if !disableInputs}
  <div class="mt-3">
    <Checkbox
      onChange={() => (useSeperateAddress = !useSeperateAddress)}
      checked={useSeperateAddress}
      >Vill du ha fakturan skickad till en annan adress än dit elen levereras? Fyll i separat
      faktureringsadress.</Checkbox
    >
  </div>
{/if}
